import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download } from 'lucide-react';
import type { SubjectWiseItem } from '../../services/api';

interface SubjectTableProps {
  data: SubjectWiseItem[];
  onSubjectClick?: (item: SubjectWiseItem) => void;
}

type SortKey = 'subjectName' | 'appeared' | 'passed' | 'passPercentage' | 'performanceIndex' | 'avgMarks';

const SubjectTable: React.FC<SubjectTableProps> = function ({ data, onSubjectClick }) {
  const [sortKey, setSortKey] = useState<SortKey>('passPercentage');
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState('');
  const [topBottomMode, setTopBottomMode] = useState<'none' | 'top' | 'bottom'>('none');
  const [topBottomN, setTopBottomN] = useState('10');
  const [passMin, setPassMin] = useState('');
  const [passMax, setPassMax] = useState('');
  const [piMin, setPiMin] = useState('');
  const [piMax, setPiMax] = useState('');

  const avgPass = data.length
    ? data.reduce(function (s, r) { return s + r.passPercentage; }, 0) / data.length
    : 0;
  const avgPI = data.length
    ? data.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / data.length
    : 0;

  function handleSort(k: SortKey) {
    if (sortKey === k) { setSortAsc(!sortAsc); } else { setSortKey(k); setSortAsc(false); }
  }

  const linkedSecKey: SortKey | null =
    sortKey === 'performanceIndex' ? 'passPercentage'
    : sortKey === 'passPercentage' ? 'performanceIndex'
    : null;

  const sortedFiltered = [...data]
    .filter(function (r) {
      const q = search.toLowerCase();
      return r.subjectName.toLowerCase().includes(q) || String(r.subjectCode).includes(q);
    })
    .sort(function (a, b) {
      const av: unknown = (a as any)[sortKey];
      const bv: unknown = (b as any)[sortKey];
      let primary: number;
      if (typeof av === 'number' && typeof bv === 'number') {
        primary = sortAsc ? av - bv : bv - av;
      } else {
        primary = sortAsc
          ? String(av ?? '').localeCompare(String(bv ?? ''))
          : String(bv ?? '').localeCompare(String(av ?? ''));
      }
      if (primary !== 0 || !linkedSecKey) return primary;
      const asc = (a as any)[linkedSecKey] as number;
      const bsc = (b as any)[linkedSecKey] as number;
      return sortAsc ? asc - bsc : bsc - asc;
    });

  const rangeFiltered = sortedFiltered.filter(function (r) {
    if (passMin !== '' && r.passPercentage < parseFloat(passMin)) return false;
    if (passMax !== '' && r.passPercentage > parseFloat(passMax)) return false;
    if (piMin !== '' && r.performanceIndex < parseFloat(piMin)) return false;
    if (piMax !== '' && r.performanceIndex > parseFloat(piMax)) return false;
    return true;
  });

  const n = Math.max(1, parseInt(topBottomN, 10) || 10);
  const displayRows =
    topBottomMode === 'top' ? rangeFiltered.slice(0, n)
    : topBottomMode === 'bottom' ? rangeFiltered.slice(-n)
    : rangeFiltered;

  function downloadCSV() {
    const headers = ['#', 'Subject', 'Code', 'Appeared', 'Passed', 'Pass%', 'PI', 'Avg Marks'];
    const rows = displayRows.map(function (r, i) {
      return [
        i + 1,
        `"${r.subjectName.replace(/"/g, '""')}"`,
        r.subjectCode,
        r.appeared,
        r.passed,
        r.passPercentage.toFixed(1),
        r.performanceIndex.toFixed(2),
        r.avgMarks !== undefined ? r.avgMarks.toFixed(1) : '',
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subject-performance.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function Th({ label, field }: { label: string; field: SortKey }) {
    const isPrimary = sortKey === field;
    const isLinked = linkedSecKey === field;
    const Icon = isPrimary ? (sortAsc ? ArrowUp : ArrowDown) : isLinked ? (sortAsc ? ArrowUp : ArrowDown) : ArrowUpDown;
    const iconClass = isPrimary ? 'w-3 h-3 text-[#F36B21]' : isLinked ? 'w-3 h-3 text-orange-300' : 'w-3 h-3 text-gray-400';
    return (
      <th
        className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
        onClick={function () { handleSort(field); }}
      >
        <span className="flex items-center gap-1">
          {label}
          <Icon className={iconClass} />
        </span>
      </th>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <h3 className="text-lg font-semibold text-gray-800">Subject-wise Performance</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{data.length} subjects</span>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search subject…"
              value={search}
              onChange={function (e) { setSearch(e.target.value); }}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
        </div>
      </div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200 text-sm">
        <div className="flex rounded-lg overflow-hidden border border-gray-200">
          {(['none', 'top', 'bottom'] as const).map(function (mode) {
            return (
              <button key={mode} onClick={function () { setTopBottomMode(mode); }} className={'px-3 py-1.5 text-xs font-medium transition-colors ' + (topBottomMode === mode ? 'bg-[#F36B21] text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                {mode === 'none' ? 'All' : mode === 'top' ? 'Top' : 'Bottom'}
              </button>
            );
          })}
        </div>
        <input type="number" min="1" value={topBottomN} onChange={function (e) { setTopBottomN(e.target.value); }} disabled={topBottomMode === 'none'} className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white disabled:opacity-40" placeholder="N" />
        <span className="text-gray-400 text-xs">Pass%</span>
        <input type="number" placeholder="Min" value={passMin} onChange={function (e) { setPassMin(e.target.value); }} className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" />
        <span className="text-gray-300">–</span>
        <input type="number" placeholder="Max" value={passMax} onChange={function (e) { setPassMax(e.target.value); }} className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" />
        <span className="text-gray-400 text-xs">PI</span>
        <input type="number" placeholder="Min" value={piMin} onChange={function (e) { setPiMin(e.target.value); }} className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" />
        <span className="text-gray-300">–</span>
        <input type="number" placeholder="Max" value={piMax} onChange={function (e) { setPiMax(e.target.value); }} className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" />
        <span className="ml-auto text-xs text-gray-400">{displayRows.length} rows</span>
        <button onClick={downloadCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F36B21] text-white text-xs font-medium rounded-lg hover:bg-orange-600 transition-colors">
          <Download className="w-3.5 h-3.5" />CSV
        </button>
      </div>
      <div className="overflow-auto max-h-[560px]">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">#</th>
              <Th label="Subject" field="subjectName" />
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Code</th>
              <Th label="Appeared" field="appeared" />
              <Th label="Passed" field="passed" />
              <Th label="Pass %" field="passPercentage" />
              <Th label="Perf. Index" field="performanceIndex" />
              <Th label="Avg Marks" field="avgMarks" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayRows.map(function (item, idx) {
              const abovePass = item.passPercentage >= avgPass;
              const abovePI = item.performanceIndex >= avgPI;
              return (
                <tr
                  key={String(item.subjectCode) + item.subjectName}
                  className={'transition-colors ' + (onSubjectClick ? 'cursor-pointer hover:bg-orange-50' : 'hover:bg-gray-50')}
                  onClick={function () { if (onSubjectClick) onSubjectClick(item); }}
                >
                  <td className="py-3 px-4 text-xs text-gray-400 font-mono">{idx + 1}</td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-gray-800">{item.subjectName}</span>
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-gray-500">{String(item.subjectCode)}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.appeared.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.passed.toLocaleString()}</td>
                  <td className="py-3 px-4">
                    <span className={'text-sm font-bold ' + (abovePass ? 'text-green-600' : 'text-red-500')}>
                      {item.passPercentage.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={'text-sm font-bold ' + (abovePI ? 'text-blue-600' : 'text-orange-500')}>
                      {item.performanceIndex.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {item.avgMarks !== undefined ? item.avgMarks.toFixed(1) : '—'}
                  </td>
                </tr>
              );
            })}
            {displayRows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-12 text-center text-gray-400 text-sm">
                  No subjects found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubjectTable;
